import { useEffect, useState } from 'react';
import api from '../services/api';
import { publicApi } from '../services/apiInstances';

/**
 * Which surface is asking.
 *
 * `admin` → `GET /api/admin/completeness/resolved` (staff screens, JWT).
 * `public` → `GET /api/public/completeness/resolved` (the /book form, no JWT).
 *
 * Two endpoints rather than one because the public answer is deliberately
 * narrower: it carries the blocking rule, the suggested fields and their labels,
 * and nothing else. A visitor to /book must not be able to read which
 * requirements the shop has waived for a named customer.
 */
export type RequirementScope = 'admin' | 'public';

export interface ResolvedRequirementsView {
  loading: boolean;
  /** Fields the server will refuse without. */
  blocking: string[];
  /** Fields the shop wants but will not block on. */
  suggested: string[];
  /** field → human label, straight from the server. Never invented here. */
  labels: Record<string, string>;
}

const EMPTY: ResolvedRequirementsView = {
  loading: false,
  blocking: [],
  suggested: [],
  labels: {},
};

/**
 * What does the shop want at this stage?
 *
 * Always asked, never assumed. A screen that hardcodes its own list is a second
 * source of truth, and the two drift — that is the defect shape behind three of
 * the bugs fixed on 2026-07-29.
 *
 * Fails OPEN: if the policy cannot be read the form stays usable and the server
 * gate does the refusing. A form that blocks because it could not reach an
 * endpoint is worse than one that lets the server answer.
 */
export function useResolvedRequirements(
  stage: string,
  opts: {
    scope?: RequirementScope;
    customerId?: number | null;
    estimateId?: number | null;
    /**
     * Ask at all? Defaults to true, so every existing caller is unchanged.
     *
     * Passed `false` by surfaces that are mounted long before they are looked at
     * — a Modal rendered with `open={false}` is the case this exists for. Asking
     * on mount there would spend a request per estimate opened, and would resolve
     * against ids the user has not acted on yet.
     */
    enabled?: boolean;
  } = {}
): ResolvedRequirementsView {
  const { scope = 'admin', customerId = null, estimateId = null, enabled = true } = opts;

  const [state, setState] = useState<ResolvedRequirementsView>({
    loading: true,
    blocking: [],
    suggested: [],
    labels: {},
  });

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      // Not "loading forever". A disabled hook reports a settled, empty answer so
      // a caller that gates on `loading` is not stuck behind a request that will
      // never be made.
      setState(EMPTY);
      return undefined;
    }

    const qs = new URLSearchParams({ stage });
    // Only the admin surface may narrow the answer to a specific record; the
    // server scopes both ids to the caller's shop and ignores anything foreign.
    if (scope === 'admin' && customerId) qs.set('customerId', String(customerId));
    if (scope === 'admin' && estimateId) qs.set('estimateId', String(estimateId));

    const request =
      scope === 'public'
        ? publicApi.get(`/public/completeness/resolved?${qs.toString()}`)
        : api.get(`/completeness/resolved?${qs.toString()}`);

    request
      .then((res: { data?: any }) => {
        if (cancelled) return;
        const rule = res.data?.blockingRule ?? { customer: [], vehicle: [] };
        setState({
          loading: false,
          blocking: [...(rule.customer ?? []), ...(rule.vehicle ?? [])],
          suggested: res.data?.suggestedFields ?? [],
          labels: res.data?.labels ?? {},
        });
      })
      .catch(() => {
        if (!cancelled) setState(EMPTY);
      });

    return () => {
      cancelled = true;
    };
  }, [stage, scope, customerId, estimateId, enabled]);

  return state;
}

export default useResolvedRequirements;
